/**
 * Sourced from Dashboard, modified for Shop.
 * https://github.com/squareup/dashboard/blob/50bf1d9c17898d9a341fa464ca94687a03645c73/frontend/dashboard/app/services/user-journey/user-journey-tracker.js#L1
 */
import { UserJourneyResult } from 'lib/user_journey/user_journey_result';
import { UserJourneyOutcome, TerminationReason } from 'constants/user_journeys';
import { UserJourneyResultConverter } from 'lib/user_journey/user_journey_converter';
import Sentry from 'application/exception_reporter';
import UserJourneyService from 'services/user_journey_service';

/**
 * Responsible for managing a mutable UserJourneyResult. The result
 * is a CDP event that is consumed by the UserJourneys app which then converts the event
 * into a Datadog metric.
 *
 * There is currently no web SDK for UserJourneys, but we can mimic how it would
 * behave. This is modeled after the mobile UserJourney framework:
 * https://github.com/squareup/ios-register/blob/main/Frameworks/UserJourney/Impl/Sources/Domain/UserJourneyTrackerImpl.swift
 */
export default class UserJourneyTracker {
  constructor() {
    /**
     * The UserJourneyDataApi is used to send UserJourneyResults to CDP.
     * We don't simply initialize it here because there is a possibility that
     * Sq.Tracking, used for accessing the CDP API key, is not available.
     * @type {UserJourneyService|null}
     */
    this.userJourneyService = null;

    /**
     * We can only have one UserJourney at a time for a given name.
     * @type {Map<TUserJourneyName, UserJourneyResult>}
     */
    this.userJourneyMap = new Map();
  }

  /**
   * Get the UserJourneyService.
   * @returns {Promise<UserJourneyService>}
   */
  async getUserJourneyService() {
    if (!this.userJourneyService) {
      this.userJourneyService = await UserJourneyService.init();
    }

    return this.userJourneyService;
  }

  /**
   * Start a new UserJourney with a given name and variant.
   * Modeled from https://github.com/squareup/ios-register/blob/main/Frameworks/UserJourney/Impl/Sources/Domain/UserJourneyTrackerImpl.swift#L34
   * @param {object} options
   * @param {TUserJourneyName} options.userJourneyName
   * @param {TUserJourneyVariant|undefined} options.userJourneyVariant
   * @returns {Promise<void>}
   */
  async startJourney({ userJourneyName, userJourneyVariant = undefined }) {
    if (!userJourneyName) {
      return;
    }

    if (this.userJourneyMap.has(userJourneyName)) {
      await this.setAsTerminatedIfRestarted(userJourneyName);
    }

    const userJourneyResult = new UserJourneyResult({
      entityId: this.entityId,
      entityType: this.entityType,
      userJourneyName,
      userJourneyVariant,
    });

    this.userJourneyMap.set(userJourneyName, userJourneyResult);
  }

  /**
   * Complete a successful UserJourney and adds additional properties.
   * @param {object} options
   * @param {TUserJourneyName} options.userJourneyName
   * @param {TUserJourneyVariant|undefined} options.userJourneyVariant
   * @param {string|number|undefined} options.duration
   * @param {Set<string>|undefined} options.tags
   * @param {TFrustrationSignal|undefined} options.frustrationSignal
   * @param {TFrictionSignal|undefined} options.frictionSignal
   * @returns {Promise<void>}
   */
  async completeUserJourneySuccessfully({
    userJourneyName,
    userJourneyVariant = undefined,
    duration = undefined,
    tags = undefined,
    frustrationSignal = undefined,
    frictionSignal = undefined,
  }) {
    const userJourneyResult = this.userJourneyMap.get(userJourneyName);

    if (!userJourneyResult) {
      return;
    }

    if (userJourneyVariant) {
      userJourneyResult.setVariant(userJourneyVariant);
    }

    if (tags) {
      userJourneyResult.addTags(tags);
    }

    if (frustrationSignal) {
      userJourneyResult.addFrustrationSignal(frustrationSignal);
    }

    if (frictionSignal) {
      userJourneyResult.addFrictionSignal(frictionSignal);
    }

    if (duration) {
      userJourneyResult.setDurationRealTimeMs(duration);
    } else {
      userJourneyResult.setDurationRealTimeMs(
        Date.now() - userJourneyResult.createdAt,
      );
    }

    userJourneyResult.setOutcome(UserJourneyOutcome.SUCCEEDED);
    await this.endJourney(userJourneyResult);
  }

  /**
   * Ends an uncompleted UserJourney.
   * @param {TUserJourneyName} userJourneyName
   * @returns {Promise<void>}
   */
  async setAsUserCanceledIfNoOutcome(userJourneyName) {
    const userJourneyResult = this.userJourneyMap.get(userJourneyName);

    if (!userJourneyResult) {
      return;
    }

    if (userJourneyResult?.outcome === undefined) {
      userJourneyResult.setOutcome(UserJourneyOutcome.USER_CANCELED);
      userJourneyResult.setDurationRealTimeMs(
        Date.now() - userJourneyResult.createdAt,
      );
      await this.endJourney(userJourneyResult);
    }
  }

  /**
   * Ends a UserJourney if we try starting a new one with the same name.
   * @param {TUserJourneyName} userJourneyName
   * @returns {Promise<void>}
   */
  async setAsTerminatedIfRestarted(userJourneyName) {
    const userJourneyResult = this.userJourneyMap.get(userJourneyName);

    if (!userJourneyResult) {
      return;
    }

    userJourneyResult.setOutcome(UserJourneyOutcome.TERMINATED);
    userJourneyResult.setTerminationReason(TerminationReason.JOURNEY_RESTARTED);
    userJourneyResult.setDurationRealTimeMs(
      Date.now() - userJourneyResult.createdAt,
    );

    await this.endJourney(userJourneyResult);
  }

  /**
   * Add a variant to a UserJourney.
   * @param {object} options
   * @param {TUserJourneyName} options.userJourneyName
   * @param {TUserJourneyVariant} options.variant
   */
  setVariant({ userJourneyName, variant }) {
    const userJourneyResult = this.userJourneyMap.get(userJourneyName);

    if (!userJourneyResult || !variant) {
      return;
    }

    userJourneyResult.setVariant(variant);
    this.userJourneyMap.set(userJourneyName, userJourneyResult);
  }

  /**
   * Add tags to a UserJourney.
   * @param {object} options
   * @param {TUserJourneyName} options.userJourneyName
   * @param {Set<string>} options.tags
   */
  addTags({ userJourneyName, tags }) {
    const userJourneyResult = this.userJourneyMap.get(userJourneyName);

    if (!userJourneyResult) {
      return;
    }

    userJourneyResult.addTags(tags);
    this.userJourneyMap.set(userJourneyName, userJourneyResult);
  }

  /**
   * Add a frustration signal to a UserJourney.
   * @param {object} options
   * @property {TUserJourneyName} userJourneyName
   * @property {TFrustrationSignal} frustrationSignal
   */
  addFrustrationSignal({ userJourneyName, frustrationSignal }) {
    const userJourneyResult = this.userJourneyMap.get(userJourneyName);

    if (!userJourneyResult) {
      return;
    }

    userJourneyResult.addFrustrationSignal(frustrationSignal);
    this.userJourneyMap.set(userJourneyName, userJourneyResult);
  }

  /**
   * Add a friction signal to a UserJourney.
   * @param {object} options
   * @param {TUserJourneyName} options.userJourneyName
   * @param {TFrictionSignal} options.frictionSignal
   */
  addFrictionSignal({ userJourneyName, frictionSignal }) {
    const userJourneyResult = this.userJourneyMap.get(userJourneyName);

    if (!userJourneyResult) {
      return;
    }

    userJourneyResult.addFrictionSignal(frictionSignal);
    this.userJourneyMap.set(userJourneyName, userJourneyResult);
  }

  /**
   * Sends UserJourney through CDP and deletes it from Map.
   * @param {UserJourneyResult} userJourneyResult
   * @returns {Promise<void>}
   */
  async endJourney(userJourneyResult) {
    await this.sendUserJourneyResultToCdp(userJourneyResult);
    this.userJourneyMap.delete(userJourneyResult.userJourneyName);
  }

  /**
   * Sends UserJourneyResult to CDP.
   * @param {UserJourneyResult} userJourneyResult
   * @returns {Promise<void>}
   */
  async sendUserJourneyResultToCdp(userJourneyResult) {
    try {
      // Send to CDP (UserJourneys namespace)
      const service = await this.getUserJourneyService();
      service.track(
        'RawUserJourneyResult',
        UserJourneyResultConverter.convertToCdpEventProperties(
          userJourneyResult,
        ),
      );
    } catch (error) {
      Sentry.capture(error);
    }
  }
}
